<!--
  OnboardingForm.vue
  ------------------
  Public onboarding/inquiry form, available in DE and EN.

  - Mode "register": Full registration of a new company (domain).
  - Mode "inquiry":  Plain product question (no account).

  Backend endpoint:
    POST /tt/api/onboarding/request   (permitAll, JSON body, see buildPayload())

  Honeypot:
    Field `salutation` (dropdown) is visually hidden.
    Real users leave it empty; bots fill it in. The server checks too,
    but the client silently fakes success without sending if it was filled.
-->

<template>
  <div class="onboarding">

    <!-- Sprachumschalter -->
    <div class="onboarding__lang">
      <button type="button" :class="{ active: locale === 'de' }" @click="setLang('de')">DE</button>
      <button type="button" :class="{ active: locale === 'en' }" @click="setLang('en')">EN</button>
    </div>

    <!-- Erfolgsbildschirm -->
    <div v-if="submitted" class="onboarding__success">
      <h2>{{ m('successTitle') }}</h2>
      <p v-if="lastMode === 'register'" v-html="successHtml('successRegister', lastEmail)"></p>
      <p v-else v-html="successHtml('successInquiry', lastEmail)"></p>
      <button type="button" class="onboarding__btn-secondary" @click="resetForm">
        {{ m('newRequest') }}
      </button>
    </div>

    <!-- Formular -->
    <form v-else class="onboarding__form" novalidate @submit.prevent="onSubmit">
      <header class="onboarding__header">
        <h1>{{ m('formTitle') }}</h1>

        <div class="onboarding__intro">
          <p v-html="m('introParagraph')"></p>
          <ul class="onboarding__bullets">
            <li v-html="m('bullet1')"></li>
            <li v-html="m('bullet2')"></li>
            <li v-html="m('bullet3')"></li>
            <li v-html="m('bullet4')"></li>
            <li v-html="m('bullet5')"></li>
          </ul>
        </div>
      </header>

      <!-- Mode-Auswahl -->
      <fieldset class="onboarding__mode">
        <legend>{{ m('modeLegend') }}</legend>
        <label>
          <input type="radio" value="register" v-model="form.mode" />
          {{ m('modeRegister') }}
        </label>
        <label>
          <input type="radio" value="inquiry" v-model="form.mode" />
          {{ m('modeInquiry') }}
        </label>
      </fieldset>

      <!-- Honeypot: visuell versteckt; Bots füllen es aus, Menschen nicht. -->
      <div class="onboarding__honeypot" aria-hidden="true">
        <label for="onb-salutation">{{ m('salutation') }}</label>
        <select
          id="onb-salutation"
          v-model="form.salutation"
          tabindex="-1"
          autocomplete="off"
        >
          <option value=""></option>
          <option value="Herr">Herr</option>
          <option value="Frau">Frau</option>
          <option value="Familie">Familie</option>
          <option value="Divers">Divers</option>
        </select>
      </div>

      <!-- E-Mail (immer Pflicht) -->
      <div class="onboarding__row">
        <label for="onb-email">
          {{ m('emailLabel') }} <span class="req">*</span>
        </label>
        <input
          id="onb-email"
          type="email"
          v-model.trim="form.email"
          autocomplete="email"
          required
        />
        <small v-if="errors.email" class="onboarding__err">{{ errors.email }}</small>
      </div>

      <!-- Passwort (nur bei Registrierung) -->
      <template v-if="isRegister">
        <div class="onboarding__row">
          <label for="onb-pw">
            {{ m('password') }} <span class="req">*</span>
          </label>
          <div class="onboarding__pw">
            <input
              id="onb-pw"
              :type="showPw ? 'text' : 'password'"
              v-model="form.password"
              autocomplete="new-password"
              minlength="8"
              required
            />
            <button
              type="button"
              class="onboarding__eye"
              @click="showPw = !showPw"
              :aria-label="showPw ? m('hidePassword') : m('showPassword')"
            >
              {{ showPw ? m('hide') : m('show') }}
            </button>
          </div>
          <small class="onboarding__hint">{{ m('passwordHint') }}</small>
          <small v-if="errors.password" class="onboarding__err">{{ errors.password }}</small>
        </div>

        <div class="onboarding__row">
          <label for="onb-pw2">
            {{ m('passwordRepeat') }} <span class="req">*</span>
          </label>
          <div class="onboarding__pw">
            <input
              id="onb-pw2"
              :type="showPw2 ? 'text' : 'password'"
              v-model="form.passwordRepeat"
              autocomplete="new-password"
              minlength="8"
              required
            />
            <button
              type="button"
              class="onboarding__eye"
              @click="showPw2 = !showPw2"
              :aria-label="showPw2 ? m('hidePassword') : m('showPassword')"
            >
              {{ showPw2 ? m('hide') : m('show') }}
            </button>
          </div>
          <small v-if="errors.passwordRepeat" class="onboarding__err">{{ errors.passwordRepeat }}</small>
        </div>

        <!-- Firma -->
        <div class="onboarding__row">
          <label for="onb-company">
            {{ m('companyName') }} <span class="req">*</span>
          </label>
          <input
            id="onb-company"
            type="text"
            v-model.trim="form.companyName"
            autocomplete="organization"
            required
          />
          <small v-if="errors.companyName" class="onboarding__err">{{ errors.companyName }}</small>
        </div>

        <!-- Admin -->
        <div class="onboarding__row onboarding__row--two">
          <div>
            <label for="onb-firstname">
              {{ m('adminFirstName') }} <span class="req">*</span>
            </label>
            <input
              id="onb-firstname"
              type="text"
              v-model.trim="form.adminFirstName"
              autocomplete="given-name"
              required
            />
            <small v-if="errors.adminFirstName" class="onboarding__err">{{ errors.adminFirstName }}</small>
          </div>
          <div>
            <label for="onb-lastname">
              {{ m('adminLastName') }} <span class="req">*</span>
            </label>
            <input
              id="onb-lastname"
              type="text"
              v-model.trim="form.adminLastName"
              autocomplete="family-name"
              required
            />
            <small v-if="errors.adminLastName" class="onboarding__err">{{ errors.adminLastName }}</small>
          </div>
        </div>

        <div class="onboarding__row">
          <label for="onb-role">
            {{ m('adminRole') }} <span class="req">*</span>
          </label>
          <input
            id="onb-role"
            type="text"
            v-model.trim="form.adminRole"
            :placeholder="m('adminRolePlaceholder')"
            required
          />
          <small class="onboarding__hint">{{ m('adminRoleHint') }}</small>
          <small v-if="errors.adminRole" class="onboarding__err">{{ errors.adminRole }}</small>
        </div>

        <!-- Homepage -->
        <div class="onboarding__row">
          <label for="onb-homepage">
            {{ m('homepage') }} <span class="req">*</span>
          </label>
          <input
            id="onb-homepage"
            type="text"
            v-model.trim="form.homepage"
            :placeholder="m('homepagePlaceholder')"
            autocomplete="url"
            required
          />
          <small class="onboarding__hint" v-html="m('homepageHint')"></small>
          <small v-if="errors.homepage" class="onboarding__err">{{ errors.homepage }}</small>
        </div>

        <!-- Verwendung -->
        <fieldset class="onboarding__row onboarding__usage">
          <legend>{{ m('usageLegend') }}</legend>
          <label>
            <input type="checkbox" v-model="form.usageChatClients" />
            {{ m('usageChatClients') }}
          </label>
          <label>
            <input type="checkbox" v-model="form.usageDocsClients" />
            {{ m('usageDocsClients') }}
          </label>
          <label>
            <input type="checkbox" v-model="form.usageDocsEmployees" />
            {{ m('usageDocsEmployees') }}
          </label>
          <label>
            <input type="checkbox" v-model="form.usageQrCodes" />
            {{ m('usageQrCodes') }}
          </label>
        </fieldset>
      </template>

      <!-- Bei Mode "inquiry" reichen ein paar Kontaktangaben -->
      <template v-else>
        <div class="onboarding__row onboarding__row--two">
          <div>
            <label for="onb-firstname">{{ m('inquiryFirstName') }}</label>
            <input
              id="onb-firstname"
              type="text"
              v-model.trim="form.adminFirstName"
              autocomplete="given-name"
            />
          </div>
          <div>
            <label for="onb-lastname">{{ m('inquiryLastName') }}</label>
            <input
              id="onb-lastname"
              type="text"
              v-model.trim="form.adminLastName"
              autocomplete="family-name"
            />
          </div>
        </div>
      </template>

      <!-- Bemerkungen / Frage -->
      <div class="onboarding__row">
        <label for="onb-remarks">
          {{ isRegister ? m('remarksRegister') : m('remarksInquiry') }}
          <span v-if="!isRegister" class="req">*</span>
        </label>
        <textarea
          id="onb-remarks"
          v-model="form.remarks"
          rows="5"
          :required="!isRegister"
        ></textarea>
        <small v-if="errors.remarks" class="onboarding__err">{{ errors.remarks }}</small>
      </div>

      <!-- AGB nur bei Registrierung -->
      <div v-if="isRegister" class="onboarding__row onboarding__terms">
        <label>
          <input type="checkbox" v-model="form.acceptTerms" />
          <span>
            {{ m('acceptTerms') }}
            <span class="req">*</span>
          </span>
        </label>
        <small v-if="errors.acceptTerms" class="onboarding__err">{{ errors.acceptTerms }}</small>
      </div>

      <!-- Globaler Fehler -->
      <div v-if="globalError" class="onboarding__err onboarding__err--global">
        {{ globalError }}
      </div>

      <!-- Submit -->
      <div class="onboarding__actions">
        <button
          type="submit"
          class="onboarding__btn-primary"
          :disabled="busy"
        >
          {{ busy
            ? m('sending')
            : (isRegister ? m('submitRegister') : m('submitInquiry')) }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { reactive, ref, computed } from 'vue'
import { useI18n } from '../i18n.js'

const SUBMIT_URL = '/tt/api/onboarding/request'

const MESSAGES = {
  de: {
    formTitle: 'Anfrage / Registrierung',
    introParagraph: 'Mit diesem Formular stellen Sie einen <strong>Registrierungsantrag</strong> oder eine reine <strong>Produktfrage</strong>. Wir bearbeiten Ihren Antrag persönlich und melden uns bei Ihnen.',
    bullet1: 'Es ist <strong>kein Abonnement</strong>.',
    bullet2: 'Es entstehen <strong>keine Fixkosten</strong>.',
    bullet3: 'Bei normaler Nutzung bleibt das Produkt <strong>dauerhaft gratis</strong>.',
    bullet4: 'Sie erhalten ein geschenktes <strong>Startguthaben von CHF&nbsp;10.&ndash;</strong>.',
    bullet5: 'Auch mit leerem Guthaben können Sie ungehindert weiterarbeiten, solange Sie die kostenpflichtige Schwelle nicht überschreiten.',

    modeLegend: 'Was möchten Sie tun?',
    modeRegister: 'Mein Unternehmen registrieren',
    modeInquiry: 'Nur eine Frage zum Produkt stellen',

    salutation: 'Anrede',

    emailLabel: 'E-Mail-Adresse',
    emailRequired: 'E-Mail-Adresse ist erforderlich.',
    emailInvalid: 'Bitte eine gültige E-Mail-Adresse eingeben.',

    password: 'Passwort',
    passwordRepeat: 'Passwort wiederholen',
    passwordHint: 'Mindestens 8 Zeichen.',
    passwordTooShort: 'Mindestens 8 Zeichen.',
    passwordMismatch: 'Die beiden Passwörter stimmen nicht überein.',
    show: 'anzeigen',
    hide: 'verbergen',
    showPassword: 'Passwort anzeigen',
    hidePassword: 'Passwort verbergen',

    companyName: 'Name der Firma',
    adminFirstName: 'Vorname des Administrators',
    adminLastName: 'Name des Administrators',
    adminRole: 'Position / Rolle in der Firma',
    adminRolePlaceholder: 'z. B. Geschäftsführer, IT-Verantwortliche/r',
    adminRoleHint: 'Weitere Administratoren können Sie später jederzeit selbst hinzufügen.',

    homepage: 'Homepage der Firma',
    homepagePlaceholder: 'z. B. da.com',
    homepageHint: 'Erlaubt sind alle Schreibweisen, z.&nbsp;B. <code>da.com</code>, <code>www.da.com</code> oder <code>https://www.da.com</code>. Wichtig: Die Domain muss Ihrer Firma gehören – sie wird Teil Ihrer eindeutigen Adresse im System.',
    homepageInvalid: 'Bitte eine gültige Domain angeben (z. B. da.com).',

    usageLegend: 'Vorgesehene Verwendung',
    usageChatClients: 'Für Chat mit Kunden',
    usageDocsClients: 'Für Dokumente an Kunden (Rechnungen, Verträge, Angebote, Einladungen)',
    usageDocsEmployees: 'Für Dokumente an Mitarbeitende (Lohnmeldungen, Lohnausweise, Krankheitszeugnisse)',
    usageQrCodes: 'Für QR-Codes',

    inquiryFirstName: 'Vorname',
    inquiryLastName: 'Name',

    remarksRegister: 'Bemerkungen / Fragen',
    remarksInquiry: 'Ihre Frage',
    remarksRequired: 'Bitte beschreiben Sie kurz Ihre Frage.',

    acceptTerms: 'Ich bestätige, dass die hier eingegebenen Daten zur Bearbeitung meines Antrags und für die anschliessende Kommunikation per E-Mail sowie im integrierten Chat verwendet werden dürfen, und dass die angegebene Firmen-Domain (Homepage) dem oben genannten Unternehmen gehört. Ich akzeptiere die AGB und die Datenschutzbestimmungen und nehme zur Kenntnis, dass für überdurchschnittliche Nutzung Kosten gemäss publizierter Preisliste entstehen können.',
    acceptTermsRequired: 'Bitte bestätigen, um fortzufahren.',
    requiredField: 'Pflichtfeld.',

    submitRegister: 'Registrierungsantrag senden',
    submitInquiry: 'Frage senden',
    sending: 'Wird gesendet…',
    networkError: 'Beim Senden ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',

    successTitle: 'Vielen Dank!',
    successRegister: 'Wir haben Ihren Registrierungsantrag erhalten und werden uns persönlich bei Ihnen melden. Sie hören in Kürze von uns unter {email}.',
    successInquiry: 'Wir haben Ihre Frage erhalten und melden uns persönlich bei Ihnen unter {email}.',
    newRequest: 'Neue Anfrage stellen',
  },
  en: {
    formTitle: 'Inquiry / Registration',
    introParagraph: 'Use this form to submit a <strong>registration request</strong> or simply ask a <strong>product question</strong>. We process your request personally and will get back to you.',
    bullet1: 'There is <strong>no subscription</strong>.',
    bullet2: '<strong>No fixed costs</strong>.',
    bullet3: 'With typical use the product remains <strong>permanently free</strong>.',
    bullet4: 'You receive a complimentary <strong>starting balance of CHF&nbsp;10.&ndash;</strong>.',
    bullet5: 'Even with an empty balance you can continue working as long as you stay below the chargeable threshold.',

    modeLegend: 'What would you like to do?',
    modeRegister: 'Register my company',
    modeInquiry: 'Just ask a product question',

    salutation: 'Salutation',

    emailLabel: 'Email address',
    emailRequired: 'Email address is required.',
    emailInvalid: 'Please enter a valid email address.',

    password: 'Password',
    passwordRepeat: 'Repeat password',
    passwordHint: 'At least 8 characters.',
    passwordTooShort: 'At least 8 characters.',
    passwordMismatch: 'The two passwords do not match.',
    show: 'show',
    hide: 'hide',
    showPassword: 'Show password',
    hidePassword: 'Hide password',

    companyName: 'Company name',
    adminFirstName: 'Administrator first name',
    adminLastName: 'Administrator last name',
    adminRole: 'Position / Role in the company',
    adminRolePlaceholder: 'e.g. Managing Director, IT lead',
    adminRoleHint: 'You can add additional administrators yourself at any time.',

    homepage: 'Company homepage',
    homepagePlaceholder: 'e.g. da.com',
    homepageHint: 'All notations are allowed, e.g. <code>da.com</code>, <code>www.da.com</code> or <code>https://www.da.com</code>. Important: the domain must belong to your company — it becomes part of your unique address in the system.',
    homepageInvalid: 'Please enter a valid domain (e.g. da.com).',

    usageLegend: 'Intended use',
    usageChatClients: 'For chat with customers',
    usageDocsClients: 'For documents to customers (invoices, contracts, quotes, invitations)',
    usageDocsEmployees: 'For documents to employees (payroll filings, salary certificates, medical certificates)',
    usageQrCodes: 'For QR codes',

    inquiryFirstName: 'First name',
    inquiryLastName: 'Last name',

    remarksRegister: 'Remarks / Questions',
    remarksInquiry: 'Your question',
    remarksRequired: 'Please briefly describe your question.',

    acceptTerms: 'I confirm that the data entered here may be used to process my request and for the subsequent communication via email and the integrated chat, and that the company domain (homepage) provided belongs to the company named above. I accept the terms and the privacy policy and acknowledge that fees according to the published price list may apply for above-average use.',
    acceptTermsRequired: 'Please confirm to continue.',
    requiredField: 'Required.',

    submitRegister: 'Send registration request',
    submitInquiry: 'Send question',
    sending: 'Sending…',
    networkError: 'An error occurred while sending. Please try again.',

    successTitle: 'Thank you!',
    successRegister: 'We have received your registration request and will personally get in touch. You will hear from us shortly at {email}.',
    successInquiry: 'We have received your question and will personally get in touch with you at {email}.',
    newRequest: 'Submit another request',
  },
}

const { locale } = useI18n()

function m(key) {
  return MESSAGES[locale.value]?.[key] ?? MESSAGES.de[key] ?? key
}

function setLang(lang) {
  if (MESSAGES[lang]) locale.value = lang
}

function escapeHtml(s) {
  return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
}

function successHtml(key, email) {
  return m(key).replace('{email}', `<strong>${escapeHtml(email)}</strong>`)
}

const emptyForm = () => ({
  mode: 'register',
  salutation: '',
  email: '',
  password: '',
  passwordRepeat: '',
  companyName: '',
  adminFirstName: '',
  adminLastName: '',
  adminRole: '',
  homepage: '',
  usageChatClients: false,
  usageDocsClients: false,
  usageDocsEmployees: false,
  usageQrCodes: false,
  remarks: '',
  acceptTerms: false,
})

const form = reactive(emptyForm())
const errors = reactive({})
const busy = ref(false)
const submitted = ref(false)
const lastMode = ref('register')
const lastEmail = ref('')
const showPw = ref(false)
const showPw2 = ref(false)
const globalError = ref('')

const isRegister = computed(() => form.mode === 'register')

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function validate() {
  Object.keys(errors).forEach((k) => delete errors[k])
  globalError.value = ''

  if (!form.email) {
    errors.email = m('emailRequired')
  } else if (!EMAIL_RE.test(form.email)) {
    errors.email = m('emailInvalid')
  }

  if (isRegister.value) {
    if (!form.password || form.password.length < 8) {
      errors.password = m('passwordTooShort')
    }
    if (form.passwordRepeat !== form.password) {
      errors.passwordRepeat = m('passwordMismatch')
    }
    if (!form.companyName) errors.companyName = m('requiredField')
    if (!form.adminFirstName) errors.adminFirstName = m('requiredField')
    if (!form.adminLastName) errors.adminLastName = m('requiredField')
    if (!form.adminRole) errors.adminRole = m('requiredField')
    if (!form.homepage) {
      errors.homepage = m('requiredField')
    } else if (!isPlausibleHomepage(form.homepage)) {
      errors.homepage = m('homepageInvalid')
    }
    if (!form.acceptTerms) {
      errors.acceptTerms = m('acceptTermsRequired')
    }
  } else {
    if (!form.remarks || form.remarks.trim().length < 3) {
      errors.remarks = m('remarksRequired')
    }
  }

  return Object.keys(errors).length === 0
}

function isPlausibleHomepage(value) {
  const cleaned = value.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0]
  return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i.test(cleaned)
}

function buildPayload() {
  return {
    mode: form.mode,
    salutation: form.salutation,
    email: form.email,
    password: isRegister.value ? form.password : null,
    companyName: isRegister.value ? form.companyName : null,
    adminFirstName: form.adminFirstName || null,
    adminLastName: form.adminLastName || null,
    adminRole: isRegister.value ? form.adminRole : null,
    homepage: isRegister.value ? form.homepage : null,
    usage: isRegister.value
      ? {
          chatClients: form.usageChatClients,
          docsClients: form.usageDocsClients,
          docsEmployees: form.usageDocsEmployees,
          qrCodes: form.usageQrCodes,
        }
      : null,
    remarks: form.remarks || '',
    acceptTerms: isRegister.value ? form.acceptTerms : null,
  }
}

async function onSubmit() {
  if (!validate()) return

  // Honeypot ausgelöst -> "still" Erfolg simulieren, nichts senden.
  if (form.salutation && form.salutation.length > 0) {
    lastMode.value = form.mode
    lastEmail.value = form.email
    submitted.value = true
    return
  }

  busy.value = true
  try {
    const res = await fetch(SUBMIT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildPayload()),
      credentials: 'same-origin',
    })
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      throw new Error(text || `HTTP ${res.status}`)
    }
    lastMode.value = form.mode
    lastEmail.value = form.email
    submitted.value = true
  } catch (err) {
    globalError.value =
      m('networkError') +
      (err && err.message ? ' (' + err.message + ')' : '')
  } finally {
    busy.value = false
  }
}

function resetForm() {
  Object.assign(form, emptyForm())
  Object.keys(errors).forEach((k) => delete errors[k])
  globalError.value = ''
  showPw.value = false
  showPw2.value = false
  submitted.value = false
}
</script>

<style scoped>
.onboarding {
  max-width: 720px;
  margin: 0 auto;
  padding: 1.5rem;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  color: #222;
  font-size: 14px;
  line-height: 1.4;
}
.onboarding h1 {
  margin: 0 0 0.5rem;
  font-size: 1.25rem;
}
.onboarding__intro p {
  margin: 0 0 0.75rem;
}
.onboarding__bullets {
  margin: 0 0 1rem;
  padding-left: 1.25rem;
}
.onboarding__bullets li {
  margin: 0.15rem 0;
}

.onboarding__lang {
  display: flex;
  gap: 4px;
  justify-content: center;
  margin-bottom: 0.75rem;
}
.onboarding__lang button {
  font: inherit;
  padding: 4px 10px;
  border: 1px solid #bbb;
  border-radius: 6px;
  background: #f4f4f4;
  cursor: pointer;
  font-size: 0.8rem;
  color: #555;
}
.onboarding__lang button.active {
  background: #1565c0;
  color: white;
  border-color: #1565c0;
}

.onboarding__form,
.onboarding__success {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.onboarding__row {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.onboarding__row--two {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
@media (max-width: 600px) {
  .onboarding__row--two {
    grid-template-columns: 1fr;
  }
}
.onboarding__row--two > div {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.onboarding label {
  font-weight: 500;
}
.req {
  color: #c00;
}
.onboarding input[type="text"],
.onboarding input[type="email"],
.onboarding input[type="password"],
.onboarding select,
.onboarding textarea {
  font: inherit;
  padding: 0.4rem 0.6rem;
  border: 1px solid #bbb;
  border-radius: 6px;
  background: #fff;
}
.onboarding input:focus,
.onboarding select:focus,
.onboarding textarea:focus {
  outline: 2px solid #4a90e2;
  border-color: #4a90e2;
}
.onboarding textarea {
  resize: vertical;
  min-height: 5.5rem;
}

.onboarding__pw {
  display: flex;
  gap: 0.5rem;
}
.onboarding__pw input {
  flex: 1;
}
.onboarding__eye {
  font: inherit;
  padding: 0 0.7rem;
  border: 1px solid #bbb;
  border-radius: 6px;
  background: #f4f4f4;
  cursor: pointer;
}

.onboarding__hint {
  color: #666;
  font-size: 0.78rem;
}
.onboarding__err {
  color: #c00;
  font-size: 0.78rem;
}
.onboarding__err--global {
  background: #fde8e8;
  border: 1px solid #f5b5b5;
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
}

.onboarding__mode {
  display: flex;
  gap: 1.25rem;
  flex-wrap: wrap;
  border: 1px solid #ddd;
  padding: 0.75rem 1rem;
  border-radius: 6px;
}
.onboarding__mode legend {
  padding: 0 0.4rem;
  font-weight: 600;
}
.onboarding__usage {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  border: 1px solid #ddd;
  padding: 0.75rem 1rem;
  border-radius: 6px;
}
.onboarding__usage legend {
  padding: 0 0.4rem;
  font-weight: 600;
}
.onboarding__terms label {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  font-weight: 400;
  line-height: 1.35;
}
.onboarding__terms input {
  margin-top: 0.25rem;
}

.onboarding__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}
.onboarding__btn-primary {
  background: #1565c0;
  color: #fff;
  border: 0;
  border-radius: 6px;
  padding: 0.55rem 1.1rem;
  font-size: 0.92rem;
  cursor: pointer;
}
.onboarding__btn-primary:disabled {
  background: #88a;
  cursor: progress;
}
.onboarding__btn-secondary {
  background: #eee;
  color: #222;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  cursor: pointer;
}

.onboarding__success h2 {
  margin: 0;
}

/* Honeypot-Feld: für echte User unsichtbar, für Bots im DOM. */
.onboarding__honeypot {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}
</style>
